import React, { useCallback, useEffect, useState } from 'react'
import classnames from 'classnames';
import { ILinkedDirectoryEntryArrayItem } from 'services/CrmApi/types/DirectoryEntry';
import { ETableSortColumn } from './types';
import { ESortOrder } from 'store/common/types';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import { Link } from 'ui/Link';
import { IScrollableTableColumn, IScrollableTableRow } from 'ui/ScrollableTable/types';
import { ScrollableTable } from 'ui/ScrollableTable/ScrollableTable';
import { useInplaceSorting } from '../../hooks/useInplaceSorting';
import { useNotifications } from 'hooks/useNotifications';
import { useUnlinkDirectoryEntriesMutation } from 'services/CrmApi/mutations/useUnlinkDirectoryEntriesMutation';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { CompanyIcon } from 'ui/Icons/components/Company.component';
import { theme } from '../../../../../tailwind.config';
import { useDefaultOnErrorHandler } from 'hooks/useDefaultOnErrorHandler';
import { formatDateDisplay } from 'utils';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { useSelector } from 'react-redux';
import { SvgIcon } from 'ui/SvgIcon';
import XIcon from 'ui/Icons/tabler/x.component.svg';

interface ILinkedCompaniesListProps {
  companies: ILinkedDirectoryEntryArrayItem[];
  onCompanyUnlink: () => void;
}

export const LinkedCompaniesList: React.FC<ILinkedCompaniesListProps> = React.memo(({ companies, onCompanyUnlink }) => {
  const { isMobile, isTablet, isDesktop, isDesktopHD } = useResponsive();
  const { sortBy, sortOrder, handleSort, sortedItems } = useInplaceSorting<ILinkedDirectoryEntryArrayItem>(ETableSortColumn.CREATED, ESortOrder.DESC, companies);
  const unlinkDirectoryEntriesMutation = useUnlinkDirectoryEntriesMutation();
  const { showSuccessNotification } = useNotifications();
  const { defaultOnErrorHandler } = useDefaultOnErrorHandler();
  const [removableLinkId, setRemovableLinkId] = useState<string|null>(null);
  const [companiesPrevCount, setCompaniesPrevCount] = useState(companies.length);
  const isSr = useSelector(AuthSelectors.isSr);
  
  useEffect(() => {
    if (companies.length > companiesPrevCount) {
      // When we add (and not remove) a link then reset the Sort
      handleSort();
    }
    setCompaniesPrevCount(companies.length);
  }, [companies, companiesPrevCount, handleSort]);  

  const handleDeleteLinkClick = useCallback((linkId: string) => {
    setRemovableLinkId(linkId);
  }, []);

  const handleDeleteLinkCancel = useCallback(() => {
    setRemovableLinkId(null);
  }, []);

  const handleDeleteLinkConfirmation = useCallback(async () => {
    try {
      await unlinkDirectoryEntriesMutation.mutateAsync(removableLinkId || '');
      showSuccessNotification('Link removed');
      onCompanyUnlink();
    } catch (error) {
      defaultOnErrorHandler(error, 'Failed to remove link');
    } finally {
      setRemovableLinkId(null);
    }
  }, [defaultOnErrorHandler, onCompanyUnlink, removableLinkId, showSuccessNotification, unlinkDirectoryEntriesMutation]);

  
  const generalColumnClassName = 'flex-1 text-gray-100 tracking-2xs text-xs leading-16px uppercase';

  const columnNameClassname = classnames('column-name', {
    'flex-1': isMobile || isTablet,
    'flex-[2]': isDesktop || isDesktopHD,
  });

  const rowNameClassname = classnames('flex row-name overflow-hidden whitespace-nowrap text-ellipsis', {
    'flex-1': isMobile || isTablet,
    'flex-[2]': isDesktop || isDesktopHD,
  });

  const linkClassname = 'font-pt-sans text-brown-100 text-[15px] underline';

  const columns: IScrollableTableColumn[] = [
    {
      id: ETableSortColumn.NAME,
      label: 'NAME',
      className: columnNameClassname,
      fieldToSortBy: ETableSortColumn.NAME,
      isVisible: true,
    },
    {
      id: ETableSortColumn.TYPE,
      label: 'TYPE',
      className: generalColumnClassName,
      fieldToSortBy: ETableSortColumn.TYPE,
      isVisible: true,
    },
    {
      id: ETableSortColumn.PUBLISHED,
      label: 'PUBLISHED',
      className: 'w-[90px]',
      fieldToSortBy: ETableSortColumn.PUBLISHED,
      isVisible: !isMobile,
    },
    {
      id: 'actions',
      label: '',
      isVisible: true,
      className: 'item-actions flex items-center relative w-[68px] px-[10px]',
    }
  ];

  const rows: IScrollableTableRow[] = sortedItems.map(company => {
  
    const name = (
      <div className={rowNameClassname} title={company.fullName}>
        {(isDesktop || isDesktopHD) && <div className="company-logo inline-flex max-w-[28px] h-auto mr-[10px] my-[2px]">
          <CompanyIcon fill={theme.colors['gray-60']} />
        </div>}
        <Link internalClassName={linkClassname} to={`/crm/company/${company.id}/edit`} target='_blank'>{company.fullName}</Link>
      </div>
    );
    const type = (
      <span className="row-type flex-1">
        {company.companyType?.value ?? ''}
      </span>
    );
    const publishedDate = (
      <span className="row-published-date">
        {company.publishedDate ? formatDateDisplay(company.publishedDate) : ''}
      </span>
    );
    const actions = (
      <div className="item-actions flex items-center relative w-[48px]">
        {!isSr && (
          <>
            <div className="w-4px h-34px rounded bg-brown-15 mx-[10px]" />
            <span className="w-6">
              <button
                className="delete rounded-full p-0 w-6 h-6 bg-white hover:bg-brown-40 border border-solid border-brown-80 cursor-pointer font-pt-sans"
                onClick={() => handleDeleteLinkClick(company.linkId)}
              >
                <SvgIcon IconComponent={XIcon} className="w-4 h-4 fill-transparent stroke-brown-80 stroke-2" />
              </button>
            </span>
          </>
        )}
      </div>
    );

    return {
      id: company.id,
      className: 'row text-black text-15px h-30px flex items-center pl-2 pr-6 mt-5px mb-5px',
      cells: [
        { id: 'name', value: name, className: rowNameClassname },
        { id: 'type', value: type, className: 'row-type flex-1' },
        { id: 'publishedDate', value: publishedDate, className: 'row-published-date w-[90px]', isVisible: !isMobile },
        { id: 'actions', value: actions }
      ],
    };
  }); 

  return (
    <>
      {removableLinkId && (
        <ConfirmationModal
          type={EConfirmationModalType.WARNING}
          className="confirm-import-modal"
          title="You are going to remove a relation. You can add it again later if you need to."
          message="Are you sure?"
          confirmButtonLabel="Yes"
          cancelButtonLabel="No"
          onConfirm={handleDeleteLinkConfirmation}
          onCancel={handleDeleteLinkCancel}
          isConfirmLoading={false}
        />
      )}
      <ScrollableTable
        rows={rows}
        columns={columns}
        sortBy={sortBy}
        sortOrder={sortOrder}
        messageWhenEmpty='No Results Found'
        onSort={handleSort}
        isLoading={false}
      />
    </>
  );
});
